import { GetRequest, PostRequest, PutRequest } from "../../services/ApiClientService"
import { useError } from "../../context/useErrorContext"
import { useLoad } from "../../context/useLoadContext"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { useTranslation } from "react-i18next"
import { ERequestType } from "../../Enum/ERequestType"
import { CourierFinancialSummary } from "../../Objects/DeliveryEntity/CourierFinancialSummary"
import { CourierPaymentRecord } from "../../Objects/DeliveryEntity/CourierPaymentRecord"

export const useCourierPayment = () => {
  const { t } = useTranslation("general")

  const { enableLoad, disableLoad } = useLoad()

  const { setError } = useError()

  const wrap = async (callback: () => Promise<void>) => {
    try {
      enableLoad()

      await callback()
    } catch (e: any) {
      const message = e instanceof Error ? e.message : t("label_unknown_error_description")

      setError(t("label_title_erro"), message)
    } finally {
      disableLoad()
    }
  }

  const handleGetFinancialSummary = async (
    filter: { startDate?: string; endDate?: string },
    onSuccess?: (rows: CourierFinancialSummary[]) => void,
  ) =>
    wrap(async () => {
      const query = new URLSearchParams()

      if (filter.startDate) {
        query.append("startDate", filter.startDate)
      }

      if (filter.endDate) {
        query.append("endDate", filter.endDate)
      }

      const url = `${API_ROUTES.COURIER_PAYMENT.COURIER_FINANCIAL_SUMMARY}${query.toString() ? `?${query.toString()}` : ""}`

      const response = await GetRequest<CourierFinancialSummary[]>(url, ERequestType.Courier)

      if (!response.success) {
        throw new Error(response.message)
      }

      onSuccess && onSuccess((response.data ?? []).map((e) => new CourierFinancialSummary(e)))
    })

  const handleGetStorePaymentControl = async (
    filter: { startDate?: string; endDate?: string } = {},
    onSuccess?: (rows: CourierFinancialSummary[]) => void,
  ) =>
    wrap(async () => {
      const query = new URLSearchParams()

      if (filter.startDate) {
        query.append("startDate", filter.startDate)
      }

      if (filter.endDate) {
        query.append("endDate", filter.endDate)
      }

      const url = `${API_ROUTES.COURIER_PAYMENT.STORE_PAYMENT_CONTROL}${query.toString() ? `?${query.toString()}` : ""}`

      const response = await GetRequest<CourierFinancialSummary[]>(url)

      if (!response.success) {
        throw new Error(response.message)
      }

      onSuccess && onSuccess((response.data ?? []).map((e) => new CourierFinancialSummary(e)))
    })

  const handleGetStorePaymentStatement = async (
    filter: { startDate?: string; endDate?: string } = {},
    onSuccess?: (rows: CourierPaymentRecord[]) => void,
  ) =>
    wrap(async () => {
      const query = new URLSearchParams()

      if (filter.startDate) {
        query.append("startDate", filter.startDate)
      }

      if (filter.endDate) {
        query.append("endDate", filter.endDate)
      }

      const url = `${API_ROUTES.COURIER_PAYMENT.STORE_PAYMENT_STATEMENT}${query.toString() ? `?${query.toString()}` : ""}`

      const response = await GetRequest<CourierPaymentRecord[]>(url)

      if (!response.success) throw new Error(response.message)

      onSuccess && onSuccess((response.data ?? []).map((e) => new CourierPaymentRecord(e)))
    })

  const handleCreateCourierPayment = async (
    payload: {
      courierId: string
      amount: number
      paymentDate: string
      notes?: string
      relatedRejectedPaymentRecordId?: number
    },
    onSuccess?: () => void,
  ) =>
    wrap(async () => {
      const response = await PostRequest<boolean>(API_ROUTES.COURIER_PAYMENT.CREATE_COURIER_PAYMENT, payload)

      if (!response.success) {
        throw new Error(response.message)
      }

      onSuccess && onSuccess()
    })

  const handleConfirmCourierPayment = async (paymentId: number, isReceived: boolean, onSuccess?: () => void) =>
    wrap(async () => {
      const response = await PutRequest<boolean>(
        API_ROUTES.COURIER_PAYMENT.CONFIRM_COURIER_PAYMENT(paymentId),
        { isReceived },
        ERequestType.Courier,
      )

      if (!response.success) {
        throw new Error(response.message)
      }

      onSuccess && onSuccess()
    })

  return {
    handleGetFinancialSummary,
    handleGetStorePaymentControl,
    handleGetStorePaymentStatement,
    handleCreateCourierPayment,
    handleConfirmCourierPayment,
  }
}
